import React from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { CubeIcon } from "@heroicons/react/24/outline";
import { ProductStoreProduct } from "../../api/fetchProductStoreProducts/types";
import { incrementProductViewCount } from "../../api/incrementProductViewCount";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatRelativeDate } from "../../utils/formatRelativeDate";
import { getProductTypeBadgeConfig } from "../../utils/productTypeBadge";

interface ProductCardProps {
  product: ProductStoreProduct;
}

export default function ProductCard({ product }: ProductCardProps) {
  const navigate = useNavigate();

  const DESCRIPTION_MAX_LENGTH = 100;
  const truncatedDescription = product.description
    ? product.description.length > DESCRIPTION_MAX_LENGTH
      ? product.description.substring(0, DESCRIPTION_MAX_LENGTH) + "..."
      : product.description
    : "";

  const handleClick = () => {
    incrementProductViewCount(product.id);
    navigate(`/product-directory/${product.id}`);
  };

  const badgeConfig = getProductTypeBadgeConfig(product.type);
  const BadgeIcon = badgeConfig.icon;

  return (
    <div
      className="block group rounded-lg transition-all bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 cursor-pointer hover:border-gray-300 dark:hover:border-slate-600"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="relative aspect-video rounded-t-lg overflow-hidden bg-gray-50 dark:bg-slate-900">
        {product.thumbnailUrl ? (
          <img
            alt={product.name}
            className="object-contain w-full h-full"
            src={product.thumbnailUrl}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            <CubeIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
        )}
        <div className="absolute bottom-2 right-2 z-10">
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full capitalize ${badgeConfig.style}`}
          >
            <BadgeIcon className="h-3 w-3" />
            {product.type}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-base mb-1 text-gray-900 dark:text-white">
          {product.name}
        </h3>

        {truncatedDescription && (
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
            {truncatedDescription}
          </p>
        )}

        <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {formatCurrency(product.price)}
        </p>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {product.viewCount || 0} views
          </span>
          <time>{formatRelativeDate(product.createdAt)}</time>
        </div>
      </div>
    </div>
  );
}
